import React from 'react';
import { isChallengeAssignment } from '../../types/assignmentPointsNote';
import type {
  AssignmentCategory,
  AssignmentCoverImage,
  AssignmentPointsItem,
  AssignmentPointsKind,
  GeneralAssignmentItem,
  ProjectAssignmentItem,
  RoutineAssignmentItem,
} from '../../types/assignmentPointsNote';
import CachedAssignmentCoverImage from '../common/CachedAssignmentCoverImage';
import {
  countGeneralPending,
  ensureRoutineDateBuckets,
  isGeneralComplete,
  isProjectComplete,
  scopedAssignedMemberIds,
} from '../../utils/assignmentPointsNoteProgress';
import ProjectAssignmentDetailSection from './ProjectAssignmentDetailSection';
import ChallengeProjectDashboard from './ChallengeProjectDashboard';
import RoutineAssignmentDashboard from './RoutineAssignmentDashboard';
const DAY_LABELS = ['일', '월', '화', '수', '목', '금', '토'];

function formatDueDate(dueDate: string): string {
  if (!dueDate) return '';
  const d = new Date(`${dueDate}T00:00:00`);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getMonth() + 1}/${d.getDate()} (${DAY_LABELS[d.getDay()]})`;
}

function DuePointsMeta({
  dueDate,
  pointsLabel,
  rewardEnabled,
  rewardCoins,
  rewardItemIds,
}: {
  dueDate: string;
  pointsLabel: string;
  rewardEnabled?: boolean;
  rewardCoins?: number;
  rewardItemIds?: string[];
}) {
  const due = formatDueDate(dueDate);
  return (
    <span className="cp-apn-block-meta">
      {due ? <span className="cp-apn-block-meta-due">{due}</span> : null}
      <span className="cp-apn-block-meta-secondary">
        {pointsLabel ? <span className="cp-apn-block-meta-points">{pointsLabel}</span> : null}
        <RewardMeta
          rewardEnabled={rewardEnabled}
          rewardCoins={rewardCoins}
          rewardItemIds={rewardItemIds}
        />
      </span>
    </span>
  );
}

function actionLabels(kind: AssignmentPointsKind | 'challenge'): {
  settings: string;
  duplicate: string;
  remove: string;
} {
  if (kind === 'challenge') {
    return {
      settings: '챌린지 프로젝트 설정',
      duplicate: '챌린지 과제 복제',
      remove: '챌린지 과제 삭제',
    };
  }
  if (kind === 'project') {
    return {
      settings: '프로젝트 과제 설정',
      duplicate: '프로젝트 과제 복제',
      remove: '프로젝트 과제 삭제',
    };
  }
  if (kind === 'routine') {
    return {
      settings: '루틴 과제 설정',
      duplicate: '루틴 과제 복제',
      remove: '루틴 과제 삭제',
    };
  }
  return {
    settings: '과제 설정',
    duplicate: '과제 복제',
    remove: '과제 삭제',
  };
}

const IconSettings = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M12 15a3 3 0 1 0 0-6 3 3 0 0 0 0 6Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1 0 2.83 2 2 0 0 1-2.83 0l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-2 2 2 2 0 0 1-2-2v-.09A1.65 1.65 0 0 0 9 19.4a1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83 0 2 2 0 0 1 0-2.83l.06.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1-2-2 2 2 0 0 1 2-2h.09A1.65 1.65 0 0 0 4.6 9a1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 0-2.83 2 2 0 0 1 2.83 0l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 2-2 2 2 0 0 1 2 2v.09A1.65 1.65 0 0 0 15 4.6a1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 0 2 2 0 0 1 0 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 2 2 2 2 0 0 1-2 2h-.09A1.65 1.65 0 0 0 19.4 15Z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconCopy = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path
      d="M8 4v12a2 2 0 0 0 2 2h8a2 2 0 0 0 2-2V7.242a2 2 0 0 0-.602-1.43L16.083 2.57A2 2 0 0 0 14.685 2H10a2 2 0 0 0-2 2z"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path
      d="M16 18v2a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2V9a2 2 0 0 1 2-2h2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
);

const IconTrash = () => (
  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" aria-hidden>
    <path d="M3 6h18" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path
      d="M19 6v14a2 2 0 01-2 2H7a2 2 0 01-2-2V6m3 0V4a2 2 0 012-2h4a2 2 0 012 2v2"
      stroke="currentColor"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
    <path d="M10 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    <path d="M14 11v6" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
  </svg>
);

/** 세로모드에서 1행을 넘치면 좌우로 흐르는 제목(넘침 폭은 CSS 변수로 전달) */
const BlockTitle: React.FC<{ title: string }> = ({ title }) => {
  const ref = React.useRef<HTMLSpanElement | null>(null);
  const [shift, setShift] = React.useState(0);

  React.useLayoutEffect(() => {
    const el = ref.current;
    if (!el) return;
    const measure = () => {
      const overflow = el.scrollWidth - el.clientWidth;
      setShift(overflow > 2 ? Math.ceil(overflow) : 0);
    };
    measure();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(el);
    return () => ro.disconnect();
  }, [title]);

  const style = shift
    ? ({
        '--cp-apn-title-shift': `-${shift}px`,
        '--cp-apn-title-duration': `${Math.max(6, Math.round(shift / 22) + 4)}s`,
      } as React.CSSProperties)
    : undefined;

  return (
    <span
      ref={ref}
      className={`cp-apn-block-title${shift ? ' is-overflowing' : ''}`}
      style={style}
      title={title || undefined}
    >
      <span className="cp-apn-block-title-text">{title || '(제목 없음)'}</span>
    </span>
  );
};

/** 과제 종류별 아이콘 — 커버 이미지 있으면 표시, 없으면 회색 플레이스홀더 */
const BlockKindIcon: React.FC<{
  kind: AssignmentPointsKind;
  coverImage?: AssignmentCoverImage | null;
  selectionCheck?: React.ReactNode;
}> = ({ kind, coverImage, selectionCheck }) => (
  <span className="cp-apn-block-icon-slot">
    {selectionCheck}
    <span className={`cp-apn-block-icon cp-apn-block-icon--${kind}`} aria-hidden>
      {coverImage ? (
        <CachedAssignmentCoverImage
          image={coverImage}
          className="cp-apn-block-icon-img"
          fallback={<span className="cp-apn-block-icon-placeholder" />}
        />
      ) : (
        <span className="cp-apn-block-icon-placeholder" />
      )}
    </span>
  </span>
);

const RewardMeta: React.FC<{
  rewardEnabled?: boolean;
  rewardCoins?: number;
  rewardItemIds?: string[];
}> = ({ rewardEnabled, rewardCoins, rewardItemIds }) => {
  const showCoins = Boolean(rewardEnabled && rewardCoins && rewardCoins >= 1);
  const itemCount = rewardItemIds?.length ?? 0;
  if (!showCoins && itemCount === 0) return null;
  return (
    <span className="cp-apn-block-reward">
      {showCoins ? (
        <span className="cp-apn-block-reward-item">
          <span className="cp-apn-block-coin" aria-hidden />
          {rewardCoins}
        </span>
      ) : null}
      {itemCount > 0 ? (
        <span className="cp-apn-block-reward-item">
          <span className="cp-apn-block-card-icon" aria-hidden />
          {itemCount}
        </span>
      ) : null}
    </span>
  );
};

type BlockActionsProps = {
  kind: AssignmentPointsKind | 'challenge';
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
};

const BlockActions: React.FC<BlockActionsProps> = ({
  kind,
  onEdit,
  onDuplicate,
  onDelete,
}) => {
  const labels = actionLabels(kind);
  return (
    <div className="cp-apn-block-actions" onClick={(e) => e.stopPropagation()}>
      <button
        type="button"
        className="cp-apn-block-action-btn"
        aria-label={labels.settings}
        data-tooltip={labels.settings}
        onClick={onEdit}
      >
        <IconSettings />
      </button>
      <button
        type="button"
        className="cp-apn-block-action-btn"
        aria-label={labels.duplicate}
        data-tooltip={labels.duplicate}
        onClick={onDuplicate}
      >
        <IconCopy />
      </button>
      <button
        type="button"
        className="cp-apn-block-action-btn is-danger"
        aria-label={labels.remove}
        data-tooltip={labels.remove}
        onClick={onDelete}
      >
        <IconTrash />
      </button>
    </div>
  );
};

function SelectionCheck({ checked }: { checked: boolean }) {
  return (
    <span
      className={`cp-apn-block-select-mark${checked ? ' is-checked' : ''}`}
      aria-label={checked ? '선택됨' : '선택 안 됨'}
    >
      <svg width="14" height="14" viewBox="0 0 24 24" fill="none" aria-hidden>
        <path
          d="M20 6L9 17l-5-5"
          stroke="currentColor"
          strokeWidth="3"
          strokeLinecap="round"
          strokeLinejoin="round"
        />
      </svg>
    </span>
  );
}

function GeneralBlock({
  item,
  category,
  coverImage,
  selected,
  isShared,
  selectionMode,
  printSelected,
  memberIdSet,
  onSelect,
  onTogglePrintSelect,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  item: GeneralAssignmentItem;
  category?: AssignmentCategory;
  coverImage?: AssignmentCoverImage | null;
  selected: boolean;
  isShared?: boolean;
  selectionMode?: boolean;
  printSelected?: boolean;
  memberIdSet?: Set<string> | null;
  onSelect: () => void;
  onTogglePrintSelect?: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const pending = countGeneralPending(item, memberIdSet);
  const complete = isGeneralComplete(item, memberIdSet);
  const total = scopedAssignedMemberIds(item.assignedMemberIds, memberIdSet).length;
  const handleClick = () => {
    if (selectionMode) {
      onTogglePrintSelect?.();
      return;
    }
    onSelect();
  };
  return (
    <div
      className={`cp-apn-block${selected && !selectionMode ? ' is-selected' : ''}${
        selectionMode && printSelected ? ' is-print-selected' : ''
      }${complete ? ' is-complete' : ''}${selectionMode ? ' is-selection-mode' : ''}`}
    >
      <button type="button" className="cp-apn-block-main" onClick={handleClick}>
        <BlockKindIcon
          kind="general"
          coverImage={coverImage}
          selectionCheck={
            selectionMode ? <SelectionCheck checked={Boolean(printSelected)} /> : null
          }
        />
        <span className="cp-apn-block-body">
          <span className="cp-apn-block-head-row">
            <span
              className="cp-apn-block-badge"
              style={{ background: category?.color || 'rgba(162, 162, 162, 1)' }}
            >
              {category?.name || '기본'}
            </span>
            {isShared ? <span className="cp-apn-block-shared-badge">공유과제</span> : null}
          </span>
          <span className="cp-apn-block-title-row">
            <BlockTitle title={item.title} />
          </span>
          <DuePointsMeta
            dueDate={item.dueDate}
            pointsLabel={`기본포인트 ${item.basePoints}`}
            rewardEnabled={item.rewardEnabled}
            rewardCoins={item.rewardCoins}
            rewardItemIds={item.rewardItemIds}
          />
          <span className={`cp-apn-block-status${complete ? ' is-done' : ' has-pending'}`}>
            {complete ? '과제 완료' : `미확인: ${pending}/${total}명`}
          </span>
        </span>
      </button>
      {!selectionMode ? (
        <BlockActions kind="general" onEdit={onEdit} onDuplicate={onDuplicate} onDelete={onDelete} />
      ) : null}
    </div>
  );
}

function ProjectBlock({
  item,
  coverImage,
  selected,
  isShared,
  expanded,
  activeSubTaskId,
  selectionMode,
  printSelected,
  memberIdSet,
  onSelect,
  onTogglePrintSelect,
  onCollapse,
  onOpenMonitoring,
  onGrantReward,
  rewardProcessing,
  onSelectSubTask,
  onEdit,
  onDuplicate,
  onDelete,
  challengeTools,
}: {
  item: ProjectAssignmentItem;
  coverImage?: AssignmentCoverImage | null;
  selected: boolean;
  isShared?: boolean;
  expanded: boolean;
  activeSubTaskId?: string | null;
  selectionMode?: boolean;
  printSelected?: boolean;
  memberIdSet?: Set<string> | null;
  rewardProcessing?: boolean;
  onSelect: () => void;
  onTogglePrintSelect?: () => void;
  onCollapse: () => void;
  onOpenMonitoring: () => void;
  onGrantReward?: () => void;
  onSelectSubTask: (subTaskId: string, index: number) => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  challengeTools?: React.ComponentProps<typeof ProjectAssignmentDetailSection>['challenge'];
}) {
  const isChallenge = isChallengeAssignment(item);
  const totalPts = item.subTasks.reduce((s, t) => s + (t.points || 0), 0);
  const accent = item.color || '#FC5555';
  const complete = isProjectComplete(item, memberIdSet);
  const handleClick = () => {
    if (selectionMode) {
      onTogglePrintSelect?.();
      return;
    }
    onSelect();
  };
  if (expanded && !selectionMode && (!isChallenge || challengeTools)) {
    return (
      <ChallengeProjectDashboard
        item={item}
        coverImage={coverImage}
        isShared={isShared}
        actions={
          <BlockActions
            kind={isChallenge ? 'challenge' : 'project'}
            onEdit={onEdit}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
          />
        }
        meta={
          <DuePointsMeta
            dueDate={item.dueDate}
            pointsLabel={`전체포인트 ${totalPts}`}
            rewardEnabled={item.rewardEnabled}
            rewardCoins={item.rewardCoins}
            rewardItemIds={item.rewardItemIds}
          />
        }
        memberIdSet={memberIdSet}
        activeSubTaskId={activeSubTaskId}
        rewardProcessing={rewardProcessing}
        challenge={isChallenge ? challengeTools : null}
        onOpenMonitoring={onOpenMonitoring}
        onGrantReward={onGrantReward}
        onSelectSubTask={onSelectSubTask}
      />
    );
  }
  return (
    <div className="cp-apn-block-wrap">
      <div
        className={`cp-apn-block${selected && !selectionMode ? ' is-selected' : ''}${
          selectionMode && printSelected ? ' is-print-selected' : ''
        }${complete ? ' is-complete' : ''}${selectionMode ? ' is-selection-mode' : ''}`}
      >
        <button type="button" className="cp-apn-block-main" onClick={handleClick}>
          <BlockKindIcon
            kind="project"
            coverImage={coverImage}
            selectionCheck={
              selectionMode ? <SelectionCheck checked={Boolean(printSelected)} /> : null
            }
          />
          <span className="cp-apn-block-body">
            <span className="cp-apn-block-head-row">
              <span
                className={`cp-apn-block-badge${isChallenge ? ' is-challenge' : ''}`}
                style={{ background: accent }}
              >
                {isChallenge ? '챌린지' : '프로젝트'}
              </span>
              {isShared ? <span className="cp-apn-block-shared-badge">공유과제</span> : null}
            </span>
            <span className="cp-apn-block-title-row">
              <BlockTitle title={item.title} />
            </span>
            <DuePointsMeta
              dueDate={item.dueDate}
              pointsLabel={`전체포인트 ${totalPts}`}
              rewardEnabled={item.rewardEnabled}
              rewardCoins={item.rewardCoins}
              rewardItemIds={item.rewardItemIds}
            />
            <span
              className={`cp-apn-block-status${complete ? ' is-done' : ''}`}
              style={complete ? undefined : { color: accent }}
            >
              {complete
                ? '과제 완료'
                : isChallenge
                  ? `챌린지: ${item.subTasks.length}개`
                  : `하위과제: ${item.subTasks.length}개`}
            </span>
          </span>
        </button>
        {!selectionMode ? (
          <BlockActions
            kind={isChallenge ? 'challenge' : 'project'}
            onEdit={onEdit}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
          />
        ) : null}
      </div>
      {!selectionMode && expanded ? (
        <ProjectAssignmentDetailSection
          item={item}
          memberIdSet={memberIdSet}
          activeSubTaskId={activeSubTaskId}
          rewardProcessing={rewardProcessing}
          onBack={onCollapse}
          onOpenMonitoring={onOpenMonitoring}
          onGrantReward={onGrantReward}
          onSelectSubTask={onSelectSubTask}
          challenge={isChallenge ? challengeTools : null}
        />
      ) : null}
    </div>
  );
}

function RoutineBlock({
  item,
  coverImage,
  selected,
  isShared,
  expanded,
  activeDate,
  selectionMode,
  printSelected,
  memberIdSet,
  onSelect,
  onTogglePrintSelect,
  onCollapse,
  onOpenMonitoring,
  onGrantReward,
  rewardProcessing,
  onSelectDate,
  onAddCustomDate,
  onEdit,
  onDuplicate,
  onDelete,
}: {
  item: RoutineAssignmentItem;
  coverImage?: AssignmentCoverImage | null;
  selected: boolean;
  isShared?: boolean;
  expanded: boolean;
  activeDate?: string | null;
  selectionMode?: boolean;
  printSelected?: boolean;
  memberIdSet?: Set<string> | null;
  rewardProcessing?: boolean;
  onSelect: () => void;
  onTogglePrintSelect?: () => void;
  onCollapse: () => void;
  onOpenMonitoring: () => void;
  onGrantReward?: () => void;
  onSelectDate: (date: string) => void;
  onAddCustomDate: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
}) {
  const ensured = ensureRoutineDateBuckets(item);
  const days = item.routineDays
    .slice()
    .sort((a, b) => a - b)
    .map((d) => DAY_LABELS[d])
    .join(', ');
  const overdue = Boolean(item.dueDate && new Date(`${item.dueDate}T23:59:59`) < new Date());
  const handleClick = () => {
    if (selectionMode) {
      onTogglePrintSelect?.();
      return;
    }
    onSelect();
  };
  if (expanded && !selectionMode) {
    return (
      <RoutineAssignmentDashboard
        item={ensured}
        coverImage={coverImage}
        isShared={isShared}
        actions={<BlockActions kind="routine" onEdit={onEdit} onDuplicate={onDuplicate} onDelete={onDelete} />}
        meta={
          <DuePointsMeta
            dueDate={item.dueDate}
            pointsLabel={`기본포인트 ${item.basePoints}`}
            rewardEnabled={item.rewardEnabled}
            rewardCoins={item.rewardCoins}
            rewardItemIds={item.rewardItemIds}
          />
        }
        days={days}
        memberIdSet={memberIdSet}
        activeDate={activeDate}
        rewardProcessing={rewardProcessing}
        onOpenMonitoring={onOpenMonitoring}
        onGrantReward={onGrantReward}
        onSelectDate={onSelectDate}
        onAddCustomDate={onAddCustomDate}
      />
    );
  }
  return (
    <div className="cp-apn-block-wrap">
      <div
        className={`cp-apn-block${selected && !selectionMode ? ' is-selected' : ''}${
          selectionMode && printSelected ? ' is-print-selected' : ''
        }${overdue ? ' is-overdue' : ''}${selectionMode ? ' is-selection-mode' : ''}`}
      >
        <button type="button" className="cp-apn-block-main" onClick={handleClick}>
          <BlockKindIcon
            kind="routine"
            coverImage={coverImage}
            selectionCheck={
              selectionMode ? <SelectionCheck checked={Boolean(printSelected)} /> : null
            }
          />
          <span className="cp-apn-block-body">
            <span className="cp-apn-block-head-row">
              <span className="cp-apn-block-badge is-routine">루틴</span>
              {isShared ? <span className="cp-apn-block-shared-badge">공유과제</span> : null}
            </span>
            <span className="cp-apn-block-title-row">
              <BlockTitle title={item.title} />
            </span>
            <DuePointsMeta
              dueDate={item.dueDate}
              pointsLabel={`기본포인트 ${item.basePoints}`}
              rewardEnabled={item.rewardEnabled}
              rewardCoins={item.rewardCoins}
              rewardItemIds={item.rewardItemIds}
            />
            <span className="cp-apn-block-days">{days || '요일 없음'}</span>
          </span>
        </button>
        {!selectionMode ? (
          <BlockActions
            kind="routine"
            onEdit={onEdit}
            onDuplicate={onDuplicate}
            onDelete={onDelete}
          />
        ) : null}
      </div>
    </div>
  );
}

export const AssignmentBlockCard: React.FC<{
  item: AssignmentPointsItem;
  category?: AssignmentCategory;
  coverImage?: AssignmentCoverImage | null;
  selected: boolean;
  /** 배정 멤버가 2개 이상 그룹에 걸쳐 있으면 공유과제 표시 */
  isShared?: boolean;
  expanded?: boolean;
  activeSubTaskId?: string | null;
  activeDate?: string | null;
  rewardProcessing?: boolean;
  selectionMode?: boolean;
  printSelected?: boolean;
  /** 현재 선택된 학급 멤버 ID — 있으면 미확인/완료 카운트를 학급 단위로 제한 */
  memberIdSet?: Set<string> | null;
  onSelect: () => void;
  onTogglePrintSelect?: () => void;
  onCollapse?: () => void;
  onOpenMonitoring?: () => void;
  onGrantReward?: () => void;
  onSelectSubTask?: (subTaskId: string, index: number) => void;
  onSelectDate?: (date: string) => void;
  onAddCustomDate?: () => void;
  onEdit: () => void;
  onDuplicate: () => void;
  onDelete: () => void;
  challengeTools?: React.ComponentProps<typeof ProjectAssignmentDetailSection>['challenge'];
}> = ({
  item,
  category,
  coverImage,
  selected,
  isShared,
  expanded = false,
  activeSubTaskId,
  activeDate,
  rewardProcessing,
  selectionMode = false,
  printSelected = false,
  memberIdSet,
  onSelect,
  onTogglePrintSelect,
  onCollapse,
  onOpenMonitoring,
  onGrantReward,
  onSelectSubTask,
  onSelectDate,
  onAddCustomDate,
  onEdit,
  onDuplicate,
  onDelete,
  challengeTools,
}) => {
  if (item.kind === 'general') {
    return (
      <GeneralBlock
        item={item}
        category={category}
        coverImage={coverImage}
        selected={selected}
        isShared={isShared}
        selectionMode={selectionMode}
        printSelected={printSelected}
        memberIdSet={memberIdSet}
        onSelect={onSelect}
        onTogglePrintSelect={onTogglePrintSelect}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
      />
    );
  }
  if (item.kind === 'project') {
    return (
      <ProjectBlock
        item={item}
        coverImage={coverImage}
        selected={selected}
        isShared={isShared}
        expanded={expanded}
        activeSubTaskId={activeSubTaskId}
        selectionMode={selectionMode}
        printSelected={printSelected}
        memberIdSet={memberIdSet}
        rewardProcessing={rewardProcessing}
        onSelect={onSelect}
        onTogglePrintSelect={onTogglePrintSelect}
        onCollapse={onCollapse ?? (() => undefined)}
        onOpenMonitoring={onOpenMonitoring ?? (() => undefined)}
        onGrantReward={onGrantReward}
        onSelectSubTask={onSelectSubTask ?? (() => undefined)}
        onEdit={onEdit}
        onDuplicate={onDuplicate}
        onDelete={onDelete}
        challengeTools={challengeTools}
      />
    );
  }
  return (
    <RoutineBlock
      item={item}
      coverImage={coverImage}
      selected={selected}
      isShared={isShared}
      expanded={expanded}
      activeDate={activeDate}
      selectionMode={selectionMode}
      printSelected={printSelected}
      memberIdSet={memberIdSet}
      rewardProcessing={rewardProcessing}
      onSelect={onSelect}
      onTogglePrintSelect={onTogglePrintSelect}
      onCollapse={onCollapse ?? (() => undefined)}
      onOpenMonitoring={onOpenMonitoring ?? (() => undefined)}
      onGrantReward={onGrantReward}
      onSelectDate={onSelectDate ?? (() => undefined)}
      onAddCustomDate={onAddCustomDate ?? (() => undefined)}
      onEdit={onEdit}
      onDuplicate={onDuplicate}
      onDelete={onDelete}
    />
  );
};
